// ============================================================================
// Navbar — slim 2026 v3 design. Context-aware: shows auth-mode nav for
// logged-in users and marketing nav for public visitors. Both share the
// same slim h-14 chrome to keep the visual language unified across the
// whole app.
// ============================================================================
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';
import logoSrc from '@/assets/snap-chart-logo-final.png';

const { FiLogOut, FiLayout, FiMenu, FiX, FiUser, FiArrowRight, FiTarget, FiGift } = FiIcons;

const Navbar = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Body scroll lock while mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch {
      navigate('/login', { replace: true });
    }
  };

  // Pages where the user is logged in / inside the app — auth nav stays
  // visible so the user never loses the "I'm logged in" SaaS context.
  // Treat as auth-area: any logged-in user on a page that isn't the
  // marketing landing (/) or the auth entry points (/login, /register).
  const PUBLIC_PATHS = ['/', '/test', '/leaderboard', '/login', '/register',
    '/forgot-password', '/reset-password', '/about', '/privacy', '/terms',
    '/risk-disclaimer', '/cookies'];
  const isPublicMarketingPage = PUBLIC_PATHS.includes(location.pathname)
    || location.pathname.startsWith('/legal/');
  const isAuthArea = !!user && !isPublicMarketingPage;

  // Auth-mode nav links (logged in, inside the app)
  const authLinks = [
    { label: 'Dashboard', to: '/dashboard', icon: FiLayout },
    { label: 'Trading',   to: '/trading',   icon: FiTarget },
    { label: 'Affiliate', to: '/affiliate', icon: FiGift },
  ];

  // Public-mode quick links (subtle, just the essentials)
  const publicLinks = [
    { label: 'free test',   to: '/test', highlight: true },
    { label: 'leaderboard', to: '/leaderboard' },
    { label: 'how',       href: '/#how' },
    { label: 'pricing',   href: '/#pricing' },
    { label: 'affiliate', to: '/affiliate' },
  ];

  const showAuthMode = user && isAuthArea;

  return (
    <>
      <motion.nav
        initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3 }}
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-gray-900/80 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          {/* Logo */}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2.5 group min-w-0">
            <img src={logoSrc} alt="SnapChart AI" className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 transition group-hover:scale-110" />
            <span className="font-bold text-sm sm:text-base md:text-lg tracking-tight truncate">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">SnapChart AI</span>
            </span>
          </Link>

          {/* Center nav — desktop only */}
          {showAuthMode ? (
            <div className="hidden md:flex items-center gap-2">
              {authLinks.map((l) => (
                <NavLink key={l.label} to={l.to}
                  className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-[0.15em] transition ${
                    isActive
                      ? 'bg-cyan-500/15 border border-cyan-400/40 text-cyan-300'
                      : 'text-gray-400 border border-transparent hover:text-cyan-400 hover:bg-white/[0.03]'
                  }`}>
                  <SafeIcon icon={l.icon} className="text-xs" /> {l.label}
                </NavLink>
              ))}
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-7 text-xs font-mono uppercase tracking-[0.15em] text-gray-400">
              {publicLinks.map((l) => (
                l.to ? (
                  <Link key={l.label} to={l.to}
                    className={`${l.highlight ? 'text-amber-300 hover:text-amber-200' : 'hover:text-cyan-400'} transition`}>
                    {l.highlight && <span className="mr-1.5">▸</span>}{l.label}
                  </Link>
                ) : (
                  <a key={l.label} href={l.href} className="hover:text-cyan-400 transition">{l.label}</a>
                )
              ))}
            </div>
          )}

          {/* Right — user menu or auth CTAs */}
          <div className="flex items-center gap-2">
            {loading ? (
              <div className="hidden sm:flex items-center gap-2 px-3 text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500">
                loading…
              </div>
            ) : user ? (
              <>
                {/* Prominent Dashboard CTA when logged in on a public/marketing
                    page — otherwise users landed here from Google auth or a
                    shared link have no visible way back to their SaaS area
                    and think they're not logged in. */}
                {!showAuthMode && (
                  <Link to="/dashboard"
                    className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:border-cyan-400/70 hover:from-cyan-500/30 hover:to-purple-500/30 transition min-h-[36px]">
                    <SafeIcon icon={FiLayout} className="text-xs" />
                    <span className="hidden sm:inline">Dashboard</span>
                    <SafeIcon icon={FiArrowRight} className="text-[10px]" />
                  </Link>
                )}
                {/* User email pill (hidden small) */}
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/5 bg-white/[0.02] text-xs text-gray-400 max-w-[180px]">
                  <SafeIcon icon={FiUser} className="text-cyan-400 text-[10px] flex-shrink-0" />
                  <span className="truncate">{user.email}</span>
                </div>
                {/* Logout */}
                <button onClick={handleLogout}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:border-red-400/40 hover:text-red-300 hover:bg-red-500/5 text-xs font-mono uppercase tracking-[0.15em] transition"
                  title="Log out">
                  <SafeIcon icon={FiLogOut} className="text-xs" />
                  <span className="hidden sm:inline">logout</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline text-xs font-mono uppercase tracking-[0.15em] text-gray-400 hover:text-cyan-400 transition px-2 py-1.5">
                  log in
                </Link>
                <Link to="/register"
                  className="inline-flex px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-cyan-400/30 text-cyan-300 text-xs font-bold hover:border-cyan-400/60 hover:from-purple-500/30 hover:to-cyan-500/30 transition items-center gap-1.5 min-h-[36px]">
                  <span className="hidden sm:inline">Start free</span><span className="sm:hidden">Sign up</span>
                  <SafeIcon icon={FiArrowRight} className="text-[10px]" />
                </Link>
              </>
            )}

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              className="md:hidden p-2 rounded-lg border border-white/10 text-gray-300 hover:text-cyan-400 hover:border-cyan-400/40 transition min-w-[40px] min-h-[40px] flex items-center justify-center">
              <SafeIcon icon={FiMenu} className="text-lg" />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Spacer so page content doesn't sit under the fixed navbar */}
      <div className="h-14" />

      {/* ═══════════ MOBILE MENU DRAWER ═══════════ */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] md:hidden bg-gray-950/95 backdrop-blur-xl flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <Link to={user ? '/dashboard' : '/'} onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2">
                <img src={logoSrc} alt="" className="h-9 w-9" />
                <span className="font-bold text-base bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  SnapChart AI
                </span>
              </Link>
              <button onClick={() => setMobileMenuOpen(false)} aria-label="Close menu"
                className="p-2 rounded-lg border border-white/10 text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-6 space-y-2">
              {showAuthMode ? (
                authLinks.map((l) => (
                  <NavLink key={l.label} to={l.to} onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => `flex items-center gap-3 p-4 rounded-xl border transition ${
                      isActive
                        ? 'border-cyan-400/40 bg-cyan-500/10 text-cyan-300'
                        : 'border-white/5 bg-white/[0.02] text-white hover:bg-white/5'
                    }`}>
                    <SafeIcon icon={l.icon} className="text-lg" />
                    <span className="font-semibold text-base flex-1">{l.label}</span>
                    <SafeIcon icon={FiArrowRight} className="text-gray-500" />
                  </NavLink>
                ))
              ) : (
                <>
                  <Link to="/test" onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 rounded-xl border border-amber-400/30 bg-amber-500/5 hover:bg-amber-500/10 transition">
                    <span className="text-2xl">▸</span>
                    <div className="flex-1">
                      <div className="font-bold text-amber-300 text-base">Free test</div>
                      <div className="text-xs text-amber-400/60 font-mono uppercase tracking-wider">how much would you save?</div>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-amber-400" />
                  </Link>
                  {[
                    { label: 'How it works', desc: 'screenshot → verdict in 2s', href: '/#how' },
                    { label: 'Pricing', desc: '30-day free trial', href: '/#pricing' },
                  ].map((l) => (
                    <a key={l.label} href={l.href} onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/5 transition">
                      <div className="flex-1">
                        <div className="font-semibold text-white text-base">{l.label}</div>
                        <div className="text-xs text-gray-500 font-mono uppercase tracking-wider">{l.desc}</div>
                      </div>
                      <SafeIcon icon={FiArrowRight} className="text-gray-500" />
                    </a>
                  ))}
                  <Link to="/affiliate" onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/5 transition">
                    <div className="flex-1">
                      <div className="font-semibold text-white text-base">Affiliate program</div>
                      <div className="text-xs text-cyan-400 font-mono uppercase tracking-wider">30% recurring</div>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-gray-500" />
                  </Link>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/5 transition mt-6">
                    <div className="flex-1 font-semibold text-gray-300 text-base">Log in</div>
                    <SafeIcon icon={FiArrowRight} className="text-gray-500" />
                  </Link>
                </>
              )}
            </nav>

            <div className="p-6 border-t border-white/5">
              {user ? (
                <>
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg border border-white/5 bg-white/[0.02] text-xs text-gray-400">
                    <SafeIcon icon={FiUser} className="text-cyan-400 flex-shrink-0" />
                    <span className="truncate">{user.email}</span>
                  </div>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 font-bold text-base">
                    <SafeIcon icon={FiLogOut} /> Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold text-base shadow-xl shadow-cyan-500/20">
                    Start free 30-day trial <SafeIcon icon={FiArrowRight} />
                  </Link>
                  <p className="text-xs text-gray-500 text-center mt-2 font-mono">no credit card · cancel anytime</p>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
